import { z } from 'zod';
import { canonicalVatId, ibanWellformed, vatIdWellformed } from './master-data.js';
import { uuid } from './posting.js';
/**
 * Die Erkennungsregeln je Kreditor — Spec Eingangsrechnungen, Nachtrag
 * 2026-10-16, `D-23`.
 *
 * **Wozu.** Die Textlage-Auslese erkannte den Kreditor eines Belegs bisher nur
 * über die IBAN und die USt-IdNr. des Belegs gegen den Kreditorenstamm. Eine
 * Rechnung ohne IBAN im Text (Lastschrift, Beleg eines Zahlungsdienstes), mit zwei IBANs oder
 * mit der Kundennummer des Hauses beim Lieferanten als einzigem Anker blieb
 * ohne Kreditor — und ohne Kreditor gibt es keine Vorbelegung. Die Regeln sind
 * **Daten je Kreditor**: Sie ergänzen den Stamm, sie ersetzen ihn nicht.
 *
 * **Sieben Arten, vier Ränge.** `iban`, `ust_id` und `glaeubiger_id` sind
 * **starke** Kennungen: Sie gehören genau einem Rechtsträger. Die vier übrigen
 * sind **schwach** — eine Kundennummer kann kurz sein und zufällig in einem
 * fremden Beleg stehen, eine Domain teilt sich ein Konzern, ein Name steht in
 * jedem Beleg, der ihn nur erwähnt. Ein schwacher Treffer ist deshalb ein
 * **Vorschlag, den der Erfasser bestätigt** und nie eine stille Übernahme.
 */
/** Die sieben Arten einer Erkennungsregel — dieselbe Liste wie der Datenbanktyp `vendor_erkennung_art`. */
export const vendorErkennungArtSchema = z.enum([
    'iban',
    'ust_id',
    'glaeubiger_id',
    'kundennummer',
    'domain',
    'name',
    'text',
]);
/**
 * Woher eine Regel stammt.
 *
 * `gelernt` entsteht beim Erfassen (nur starke Kennungen), `manuell` in der
 * Pflege auf der Kreditorseite. `stamm` ist für Zusatzkennungen vorgesehen, die
 * ein Mensch aus dem Kreditorenstamm heraus nachträgt; **kein Weg schreibt ihn
 * heute** — der Wert steht, damit die Spalte nicht um einen vierten Zustand
 * erweitert werden muss, sobald es einen gibt.
 */
export const vendorErkennungQuelleSchema = z.enum(['stamm', 'gelernt', 'manuell']);
/**
 * Der Rang einer Art in der Trefferordnung — kleiner ist besser.
 *
 * `iban`, `ust_id` und `glaeubiger_id` stehen auf **einer** Stufe: Zeigen zwei
 * davon auf verschiedene Kreditoren, gewinnt keine (Widerspruch), und es gibt
 * keinen Vorschlag. `name` und `text` teilen sich die letzte Stufe aus
 * demselben Grund — ein Name ist nichts Genaueres als eine Textstelle.
 */
export const VENDOR_ERKENNUNG_RANG = {
    iban: 1,
    ust_id: 1,
    glaeubiger_id: 1,
    kundennummer: 2,
    domain: 3,
    name: 4,
    text: 4,
};
/** Rang 1 ist stark, alles darunter schwach. */
export function vendorErkennungStaerke(art) {
    return VENDOR_ERKENNUNG_RANG[art] === 1 ? 'stark' : 'schwach';
}
/**
 * Die Wortfolge eines Textes in der Form, in der Belegtext und Regel
 * verglichen werden.
 *
 * Kleinschreibung, `ß` zu `ss`, Umlaute zu `ae`/`oe`/`ue`, übrige Akzente weg,
 * alles ausser Buchstaben und Ziffern zu einem Leerzeichen, mehrfacher
 * Leerraum zu einem. **Dieselbe Rechnung auf beiden Seiten** — eine Regel
 * `Müller & Söhne GmbH` trifft damit `MUELLER + SOEHNE GMBH` und `Mueller &
 * Soehne GmbH` ebenso. Die gespeicherte Regel ist bereits so normalisiert und
 * deshalb in Kleinbuchstaben.
 */
export function normalisiereWortfolge(text) {
    return text
        .toLowerCase()
        .replace(/ß/g, 'ss')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/**
 * Freemail- und Massenanbieter: Eine Domain davon sagt nichts über den
 * Absender. Eine Regel auf `gmail.com` würde jeden Beleg treffen, der eine
 * Gmail-Adresse nennt.
 */
const FREIE_DOMAINS = new Set([
    'gmail.com',
    'googlemail.com',
    'gmx.de',
    'gmx.net',
    'gmx.at',
    'gmx.ch',
    'web.de',
    't-online.de',
    'freenet.de',
    'outlook.com',
    'outlook.de',
    'hotmail.com',
    'hotmail.de',
    'live.com',
    'live.de',
    'yahoo.com',
    'yahoo.de',
    'icloud.com',
    'me.com',
    'aol.com',
    'aol.de',
    'posteo.de',
    'mail.de',
    'protonmail.com',
    'proton.me',
]);
/** Die Domain einer Adresse in der Form, in der sie gespeichert und verglichen wird. */
export function normalisiereDomain(roh) {
    return roh
        .trim()
        .toLowerCase()
        .replace(/^[a-z]+:\/\//, '')
        .replace(/^.*@/, '')
        .replace(/[/?#:].*$/, '')
        .replace(/^www\./, '')
        .replace(/\.$/, '');
}
const DOMAIN_FORM = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;
const GLAEUBIGER_FORM = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{3}[A-Z0-9]{1,28}$/;
const KUNDENNUMMER_FORM = /^[A-Z0-9][A-Z0-9./-]{2,39}$/;
const WORTFOLGE_FORM = /^[a-z0-9]+(?: [a-z0-9]+)*$/;
/**
 * Bringt einen eingegebenen Wert in die gespeicherte Form — **eine Rechnung
 * für Oberfläche, Dienst und Lernen**.
 *
 * Die Datenbank hält dieselben Formen als CHECK-Bedingung
 * (`vendor_erkennung_wert_*`, Migration 0324); diese Funktion sorgt dafür,
 * dass der Dienst sie nie erst dort trifft.
 */
export function normalisiereErkennungswert(art, roh) {
    switch (art) {
        case 'iban': {
            const wert = roh.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (!ibanWellformed(wert)) {
                return { ok: false, grund: 'Die IBAN hat eine unzulässige Form oder eine falsche Prüfsumme.' };
            }
            return { ok: true, wert };
        }
        case 'ust_id': {
            const wert = canonicalVatId(roh);
            if (!vatIdWellformed(wert)) {
                return {
                    ok: false,
                    grund: 'Die USt-IdNr. hat eine unzulässige Form oder eine falsche Prüfziffer.',
                };
            }
            return { ok: true, wert };
        }
        case 'glaeubiger_id': {
            const wert = roh.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (!GLAEUBIGER_FORM.test(wert)) {
                return {
                    ok: false,
                    grund: 'Die Gläubiger-ID hat eine unzulässige Form. Sie beginnt mit Länderkürzel und zwei Prüfziffern, ' +
                        'danach folgen drei Zeichen Geschäftsbereich und die Kennung des Gläubigers.',
                };
            }
            return { ok: true, wert };
        }
        case 'kundennummer': {
            const wert = roh.toUpperCase().replace(/\s+/g, '');
            if (!KUNDENNUMMER_FORM.test(wert)) {
                return {
                    ok: false,
                    grund: 'Die Kundennummer braucht drei bis vierzig Zeichen aus Buchstaben, Ziffern, Punkt, Strich und Schrägstrich.',
                };
            }
            return { ok: true, wert };
        }
        case 'domain': {
            const wert = normalisiereDomain(roh);
            if (!DOMAIN_FORM.test(wert)) {
                return { ok: false, grund: 'Das ist keine Domain, zum Beispiel beispiel.de.' };
            }
            if (FREIE_DOMAINS.has(wert)) {
                return {
                    ok: false,
                    grund: 'Die Domain eines freien E-Mail-Anbieters sagt nichts über den Absender.',
                };
            }
            return { ok: true, wert };
        }
        case 'name':
        case 'text': {
            const wert = normalisiereWortfolge(roh);
            const mindest = art === 'name' ? 4 : 6;
            if (wert.length < mindest || !/[a-z]/.test(wert)) {
                return {
                    ok: false,
                    grund: art === 'name'
                        ? 'Der Name ist zu kurz, um einen Kreditor sicher zu erkennen. Mindestens vier Zeichen mit einem Buchstaben.'
                        : 'Die Textstelle ist zu kurz, um einen Kreditor sicher zu erkennen. Mindestens sechs Zeichen mit einem Buchstaben.',
                };
            }
            return { ok: true, wert };
        }
    }
}
/** Ob ein bereits gespeicherter Wert die Form seiner Art hält — für die Gegenprobe in Tests. */
export function istErkennungswertKanonisch(art, wert) {
    if (art === 'name' || art === 'text')
        return WORTFOLGE_FORM.test(wert);
    const ergebnis = normalisiereErkennungswert(art, wert);
    return ergebnis.ok && ergebnis.wert === wert;
}
/**
 * Eine neue Regel — Art und Wert. **Der Wert wird normalisiert**, der Dienst
 * parst deshalb ein zweites Mal (`pruefeRumpf` gibt die Eingabe ungeprüft
 * zurück).
 */
export const vendorErkennungInputSchema = z
    .object({
    art: vendorErkennungArtSchema,
    wert: z.string().max(200),
})
    .strict()
    .transform((eingabe, ctx) => {
    const ergebnis = normalisiereErkennungswert(eingabe.art, eingabe.wert);
    if (!ergebnis.ok) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['wert'], message: ergebnis.grund });
        return z.NEVER;
    }
    return { art: eingabe.art, wert: ergebnis.wert };
});
/** Eine Erkennungsregel, wie die Pflege sie zeigt. */
export const vendorErkennungRegelSchema = z
    .object({
    id: uuid,
    vendorId: uuid,
    art: vendorErkennungArtSchema,
    wert: z.string(),
    quelle: vendorErkennungQuelleSchema,
    aktiv: z.boolean(),
    createdAt: z.string(),
    /** Anzeigename dessen, der die Regel angelegt hat; `null` für eine Regel ohne auflösbaren Benutzer. */
    createdBy: z.string().nullable(),
    deaktiviertAm: z.string().nullable(),
    deaktiviertVon: z.string().nullable(),
})
    .strict();
/** Die Regeln eines Kreditors — aktive zuerst, innerhalb davon die jüngste zuerst. */
export const vendorErkennungListeSchema = z
    .object({ regeln: z.array(vendorErkennungRegelSchema).readonly() })
    .strict();
//# sourceMappingURL=vendor-erkennung.js.map